import React, { ChangeEvent, KeyboardEvent } from 'react';
import { Checkbox, Col, Input, Row, Table, Typography } from 'antd';
import { DownOutlined } from '@ant-design/icons/lib';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import debounce from 'lodash.debounce';
import { DragHandle } from 'entities/Asset/components/DnD/AssetDnDCommon';
import { IAssetCategory, IAssetMenuItem } from 'entities/Asset/Asset.models';
import { ECommonEntityStatus } from 'entities/Bitecoin/Bitecoin.models';

const SortableItem = SortableElement(({ index, ...props }: any) => <tr {...props} key={index} />);
const SortableContainerComponent = SortableContainer(({ index, ...props }: any) => <tbody {...props} key={index} />);

interface IComponentProps {
  onItemChange: (categoryItem: IAssetCategory) => void;
  item: IAssetCategory;
  disabled?: boolean;
}

export class AssetChildSortableTable extends React.Component<IComponentProps, { collapsed: boolean }> {
  public debounceOnChangeToggle: any;
  public debounceOnChangeIsInMealBuilderToggle: any;
  constructor(props: IComponentProps) {
    super(props);
    this.state = { collapsed: false };
    this.debounceOnChangeToggle = debounce(this.isInMenuToggle, 300);
    this.debounceOnChangeIsInMealBuilderToggle = debounce(this.isInMealBuilderToggle, 300);
  }
  render() {
    const { item, disabled } = this.props;
    const { collapsed } = this.state;
    const { name: categoryName, isMealBuilder, menuItemsToAsset, status } = item;
    const isCategoryActive = status === ECommonEntityStatus.Active;
    const titleForCategory = isCategoryActive ? categoryName : `Category is ${status}`;

    const DraggableContainer = (props: any) => {
      return (
        <SortableContainerComponent
          {...props}
          useDragHandle
          distance={4}
          lockAxis="y"
          helperClass="row-dragging"
          onSortEnd={this.onSortEnd}
          shouldCancelStart={() => !!disabled}
        />
      );
    };

    const columns = [
      {
        title: disabled ? null : <DragHandle />,
        dataIndex: 'sort',
        render: () => (disabled ? null : <DragHandle />),
        width: '40px',
      },
      {
        title: (
          <Row justify="space-between" align="middle" className="asset-dnd-category-header">
            <div style={{ width: '180px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <DownOutlined rotate={collapsed ? -90 : 0} />
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                title={titleForCategory}
                className="mb-0"
                disabled={!isCategoryActive}
                delete={!isCategoryActive}
                style={{ flex: 1, minWidth: 0 }}
              >
                {categoryName}
              </Typography.Paragraph>
            </div>
            <Col span={6}>
              <Row gutter={10}>
                <Col span={12}>Menu price</Col>
                {isMealBuilder && (
                  <Col span={12}>Builder price</Col>
                )}
              </Row>
            </Col>
            <Col span={8} />
          </Row>
        ),
        render: (item: IAssetMenuItem) => {
          const { name, status, isInMenu, isInMealBuilder, id, price, mealBuilderPrice } = item;
          const isMenuItemActive = status === ECommonEntityStatus.Active;
          const title = isMenuItemActive ? name : `Menu item was ${status}`;

          return (
            <Row justify="space-between" align="middle">
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                title={title}
                className="mb-0"
                disabled={!isMenuItemActive}
                delete={!isMenuItemActive}
                style={{ width: '180px' }}
              >
                {name}
              </Typography.Paragraph>
              <Col span={6}>
                <Row gutter={10}>
                  {isMenuItemActive && (
                    <>
                      <Col span={12}>
                        <Input
                          type="number"
                          prefix="P"
                          key={id}
                          defaultValue={price}
                          onBlur={(e: ChangeEvent<HTMLInputElement>) => this.onPriceChange(e.target.value, id)}
                          onPressEnter={(e: KeyboardEvent<HTMLInputElement>) => e.preventDefault()}
                          disabled={disabled}
                        />
                      </Col>
                      <Col span={12}>
                        {isMealBuilder && (
                          <Input
                            type="number"
                            prefix="P"
                            key={id}
                            defaultValue={mealBuilderPrice ?? 0}
                            onBlur={(e: ChangeEvent<HTMLInputElement>) =>
                              this.onMealBuilderPriceChange(e.target.value, id)
                            }
                            onPressEnter={(e: KeyboardEvent<HTMLInputElement>) => e.preventDefault()}
                            disabled={disabled}
                          />
                        )}
                      </Col>
                    </>
                  )}
                </Row>
              </Col>
              <Col span={8}>
                <Row>
                  {isMenuItemActive && (
                    <>
                      <Col span={12}>
                        <Checkbox
                          key={id}
                          checked={isInMenu}
                          onChange={(e) => this.debounceOnChangeToggle(e, id)}
                          disabled={!isMenuItemActive || disabled}
                        >
                          Add to menu
                        </Checkbox>
                      </Col>
                      <Col span={12}>
                        {isMealBuilder && (
                          <Checkbox
                            key={id}
                            checked={isInMealBuilder}
                            onChange={(e) => this.debounceOnChangeIsInMealBuilderToggle(e, id)}
                            disabled={!isMenuItemActive || disabled}
                          >
                            Add to builder
                          </Checkbox>
                        )}
                      </Col>
                    </>
                  )}
                </Row>
              </Col>
            </Row>
          );
        },
      },
    ];

    return (
      <Table
        pagination={false}
        dataSource={menuItemsToAsset}
        columns={columns}
        rowKey="id"
        className={collapsed ? 'asset-dnd-collapsed' : undefined}
        onHeaderRow={() => ({ onClick: this.toggleCollapsed, style: { cursor: 'pointer' } })}
        components={{
          body: {
            wrapper: DraggableContainer,
            row: this.DraggableBodyRow,
          },
        }}
      />
    );
  }

  toggleCollapsed = () => this.setState((state) => ({ collapsed: !state.collapsed }));

  onPriceChange = (priceValue: string, id: string) => {
    const { onItemChange, item } = this.props;
    const price = parseInt(priceValue);
    const newMenuItemsArray = item.menuItemsToAsset.map((item) => (item.id === id ? { ...item, price } : item));

    onItemChange({ ...item, menuItemsToAsset: newMenuItemsArray });
  };

  onMealBuilderPriceChange = (priceValue: string, id: string) => {
    const { onItemChange, item } = this.props;
    const mealBuilderPrice = parseInt(priceValue);
    const newMenuItemsArray = item.menuItemsToAsset.map((item) =>
      item.id === id ? { ...item, mealBuilderPrice } : item
    );

    onItemChange({ ...item, menuItemsToAsset: newMenuItemsArray });
  };

  isInMenuToggle = (e: CheckboxChangeEvent, id: string) => {
    const { onItemChange, item } = this.props;
    const isInMenu = e.target.checked;
    const newMenuItemsArray = item.menuItemsToAsset.map((item) => (item.id === id ? { ...item, isInMenu } : item));

    onItemChange({ ...item, menuItemsToAsset: newMenuItemsArray });
  };

  isInMealBuilderToggle = (e: CheckboxChangeEvent, id: string) => {
    const { onItemChange, item } = this.props;
    const isInMealBuilder = e.target.checked;
    const newMenuItemsArray = item.menuItemsToAsset.map((item) =>
      item.id === id ? { ...item, isInMealBuilder } : item
    );

    onItemChange({ ...item, menuItemsToAsset: newMenuItemsArray });
  };

  onSortEnd = ({ oldIndex, newIndex }: any) => {
    const { onItemChange, item } = this.props;

    if (oldIndex !== newIndex) {
      // ponytail: порядковые номера проставит AssetSortableParent.onItemChange по полному списку
      const newData = arrayMove([...item.menuItemsToAsset], oldIndex, newIndex);

      onItemChange({ ...item, menuItemsToAsset: newData });
    }
  };

  DraggableBodyRow = (props: any) => {
    const { item } = this.props;
    const index = item.menuItemsToAsset.findIndex((x) => x.id === props['data-row-key']);

    return <SortableItem {...props} key={index} index={index} />;
  };
}
